import { AttachmentField } from '$src/components/common/AttachmentField'
import { useAttachments } from '$src/lib/hooks/useAttachments'
import { maintenanceLogHooks, maintenanceTaskHooks } from '$src/lib/trpcHooks'
import {
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core'
import { DateInput } from '@mantine/dates'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { AppModal } from '@markku/components'
import { categoryOptions } from '@markku/utils'
import { IconCoinEuro } from '@tabler/icons-react'
import { useEffect, useState } from 'react'

interface CostEntryModalProps {
  propertyId: number
  opened: boolean
  onClose: () => void
}

interface CostFormData {
  name: string
  completedDate: string
  actualCost: number | undefined
  category: string
  notes: string
}

export function CostEntryModal({
  propertyId,
  opened,
  onClose,
}: CostEntryModalProps) {
  const [isSaving, setIsSaving] = useState(false)
  const attachments = useAttachments('maintenance_log')

  const form = useForm<CostFormData>({
    initialValues: {
      name: '',
      completedDate: new Date().toISOString().split('T')[0],
      actualCost: undefined,
      category: '',
      notes: '',
    },
    validate: {
      name: (value) =>
        !value.trim() ? 'Kustannuksen nimi on pakollinen' : null,
      completedDate: (value) => (!value ? 'Päivämäärä on pakollinen' : null),
    },
  })

  const { mutateAsync: createTask } = maintenanceTaskHooks.useCreate()
  const { mutateAsync: createLog } = maintenanceLogHooks.useCreate()

  // Reset form when modal opens
  useEffect(() => {
    if (opened) {
      form.reset()
      attachments.setInitialAttachments([])
      form.clearErrors()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened])

  const handleSave = async () => {
    // Validate form
    if (form.validate().hasErrors) {
      notifications.show({
        title: 'Virhe',
        message: 'Tarkista lomakkeen tiedot',
        color: 'red',
      })
      return
    }

    setIsSaving(true)
    try {
      // Create minimal task (deadline = completed date, no estimated cost)
      const task = await createTask({
        data: {
          propertyId,
          name: form.values.name,
          deadline: form.values.completedDate,
          category: form.values.category || undefined,
          description: form.values.notes || undefined,
          estimatedCost: undefined,
        },
      })

      // Create completion log
      await createLog({
        data: {
          propertyId,
          maintenanceTaskId: task.id,
          taskNameSnapshot: form.values.name,
          descriptionSnapshot: form.values.notes || undefined,
          completedDate: form.values.completedDate,
          notes: form.values.notes || undefined,
          actualCost: form.values.actualCost?.toString(),
        },
        attachments: attachments.getChanges(),
      })

      notifications.show({
        title: 'Onnistui',
        message: 'Kustannus tallennettu',
        color: 'green',
      })

      onClose()
    } catch (error) {
      console.error('Error saving cost:', error)
      notifications.show({
        title: 'Virhe',
        message: 'Kustannuksen tallentaminen epäonnistui',
        color: 'red',
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title="Lisää kustannus"
      confirmProps={{
        label: 'Tallenna',
        onClick: handleSave,
        loading: isSaving,
      }}
      cancelProps={{
        label: 'Peruuta',
      }}
      showFooter={true}
    >
      <Stack gap="md">
        <TextInput
          label="Kustannuksen nimi"
          placeholder="Esim. Putkiremontti, LVI-huolto"
          value={form.values.name}
          onChange={(e) => form.setFieldValue('name', e.currentTarget.value)}
          error={form.errors.name}
          required
          disabled={isSaving}
        />

        <DateInput
          label="Päivämäärä"
          placeholder="Valitse päivämäärä"
          value={
            form.values.completedDate
              ? new Date(form.values.completedDate)
              : new Date()
          }
          onChange={(value) => {
            form.setFieldValue('completedDate', value || '')
          }}
          error={form.errors.completedDate}
          required
          disabled={isSaving}
        />

        <NumberInput
          label="Kustannus"
          placeholder="0"
          leftSection={<IconCoinEuro size={16} />}
          rightSection={
            <Text size="sm" c="dimmed">
              €
            </Text>
          }
          value={form.values.actualCost || ''}
          onChange={(value) =>
            form.setFieldValue(
              'actualCost',
              typeof value === 'number' ? value : undefined,
            )
          }
          min={0}
          max={100000}
          decimalScale={2}
          disabled={isSaving}
          description="Jätä tyhjäksi jos ei aiheuttanut kustannuksia"
        />

        <Select
          label="Kohde"
          placeholder="Valitse kohde"
          data={categoryOptions}
          value={form.values.category}
          onChange={(value) => form.setFieldValue('category', value || '')}
          clearable
          disabled={isSaving}
        />

        <Textarea
          label="Muistiinpanot"
          placeholder="Lisää tarkempi kuvaus..."
          value={form.values.notes}
          onChange={(e) => form.setFieldValue('notes', e.currentTarget.value)}
          minRows={3}
          disabled={isSaving}
        />

        <div>
          <Stack gap="xs">
            <TextInput label="Liitteet" disabled style={{ display: 'none' }} />
            <AttachmentField
              attachments={attachments.attachments}
              isUploading={attachments.isUploading}
              uploadFile={attachments.uploadFile}
              removeAttachment={attachments.removeAttachment}
              disabled={isSaving}
            />
          </Stack>
        </div>
      </Stack>
    </AppModal>
  )
}
